import type { GroupRef, PersonSummary, Role } from '@ralph/contracts'
import clsx from 'clsx'
import { Download, Search, X } from 'lucide-react'
import { useId, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { PeopleFilters } from '@/api/people.ts'
import { peopleExportUrl } from '@/api/people.ts'
import { GroupLabel } from '@/components/groups/GroupLabel.tsx'
import { AccessPanel } from '@/components/people/AccessPanel.tsx'
import { PersonPanel } from '@/components/people/PersonPanel.tsx'
import { RoleChipRow } from '@/components/people/RoleChips.tsx'
import { riseDelay, StudioRow } from '@/components/ui/admin-chrome.tsx'
import { Badge, Button, EmptyState, Input, Select, Spinner } from '@/components/ui/primitives.tsx'
import { useSession } from '@/hooks/use-api.ts'
import { useGroups } from '@/hooks/use-groups.ts'
import { usePeople, usePeopleCounts, usePerson } from '@/hooks/use-people.ts'
import { useI18n } from '@/i18n/index.ts'

/**
 * The people plane (PRD-21).
 *
 * ── Why this is a page and not an eighth tab of the agent studio ──
 *
 * The studio's own chrome explains why its tabs cohere: it is one instrument for administering
 * agents. A page about human beings inside a surface titled "Agent studio" is a category error
 * the reader has to translate every time. `/people` sits beside `/governance` and `/activity`,
 * which are already role-gated top-level entries.
 *
 * ── The shape ──
 *
 * A list on the left, one person on the right. Below `lg` the detail is a full-width push with a
 * back affordance, because a 360 px column cannot hold a role editor next to a list and
 * pretending otherwise produces two unusable halves.
 *
 * ── What the reader may do ──
 *
 * Reads are for auditors and operators; every write is an administrator's. `canWrite` is passed
 * down rather than re-derived per component, and the controls it hides are ABSENT rather than
 * disabled — a disabled button an auditor can never enable is a promise the surface cannot keep.
 * The server refuses either way; this only decides what is worth showing.
 *
 * ── Groups ──
 *
 * Each row names the groups the person is in, and the list filters by one, kept in the URL
 * (`/people?group=…`) so a group's page can link to "everyone in it, with their roles" and an
 * export can be scoped to one group. Groups never confer a role (PRD-26 D-24); the row shows
 * both because an administrator's question is usually about both at once.
 */

const ROLE_FILTERS: Role[] = ['admin', 'operator', 'auditor']

/** How many of a person's groups a row names before it says "+2". */
const ROW_GROUPS = 2

/** A row's groups: the first few by name, then how many more. */
const RowGroups = ({ groups, prefix }: { groups: readonly GroupRef[]; prefix?: string }) => {
  const fmt = useI18n()
  const t = fmt.t.people

  return groups.length === 0 ? null : (
    <p className="mt-1.5 flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1 text-[12px] text-fg-muted">
      {prefix && <span className="text-fg-subtle">{prefix}</span>}
      {groups.slice(0, ROW_GROUPS).map((entry) => (
        <GroupLabel key={entry.id} group={entry} size={16} className="max-w-[11rem]" />
      ))}
      {groups.length > ROW_GROUPS && (
        <span className="readout text-fg-subtle">
          <span aria-hidden>{t.list.moreGroups(fmt, groups.length - ROW_GROUPS)}</span>
          <span className="sr-only">{t.list.moreGroupsLabel(fmt, groups.length - ROW_GROUPS)}</span>
        </span>
      )}
    </p>
  )
}

export const PeoplePage = () => {
  const fmt = useI18n()
  const t = fmt.t.people
  const session = useSession()
  const roles = session.data?.user?.roles ?? []
  const canWrite = roles.includes('admin')
  const canRead = canWrite || roles.includes('operator') || roles.includes('auditor')

  const searchId = useId()
  const groupId = useId()
  const [params, setParams] = useSearchParams()
  const [view, setView] = useState<'people' | 'access'>('people')
  const [filters, setFilters] = useState<PeopleFilters>({ q: '', role: '', status: '' })
  const [selected, setSelected] = useState<string | null>(null)

  // The group filter lives in the URL; everything else is the moment's search.
  const group = params.get('group') ?? ''
  const scoped: PeopleFilters = group === '' ? filters : { ...filters, group }
  const setGroup = (id: string) =>
    setParams(
      (current) => {
        const next = new URLSearchParams(current)
        if (id === '') next.delete('group')
        else next.set('group', id)
        return next
      },
      { replace: true }
    )

  const people = usePeople(scoped, canRead && view === 'people')
  const counts = usePeopleCounts(canRead)
  const person = usePerson(selected)
  const groups = useGroups({ scope: 'all', limit: 200 }, canRead && view === 'people')

  if (!canRead) {
    return (
      <div className="fade-in min-h-0 flex-1 overflow-y-auto px-5 py-10">
        <EmptyState title={t.deniedTitle} body={t.deniedBody} />
      </div>
    )
  }

  const items: PersonSummary[] = (people.data?.pages ?? []).flatMap((page) => page.items)
  const tally = counts.data

  const openPerson = (id: string) => {
    setSelected(id)
    setView('people')
  }

  return (
    <div className="fade-in flex min-h-0 flex-1 flex-col">
      <header className="shrink-0 border-b border-rule px-5 pt-5 sm:px-8">
        <div className="flex flex-wrap items-start gap-x-4 gap-y-3">
          {/* A basis, so on a phone the actions wrap below the lead rather than squeezing it
              into a narrow column beside them. */}
          <div className="min-w-0 flex-1 basis-[16rem]">
            <h1 className="display text-[30px] sm:text-[38px]">{t.title}</h1>
            <p className="mt-[7px] max-w-[560px] text-pretty text-[13.5px] leading-[1.5] text-fg-muted">{t.lead}</p>
          </div>

          {/* Two segmented pills rather than a tablist, the same choice the governance page
              made and for the same reason: a half-built tablist owes the reader roving focus
              and arrow keys, and `aria-pressed` promises only what is actually here. */}
          <div className="flex flex-wrap items-center gap-1.5">
            {(['people', 'access'] as const).map((value) => (
              <button
                key={value}
                type="button"
                aria-pressed={view === value}
                onClick={() => setView(value)}
                className={clsx(
                  'inline-flex h-[30px] items-center gap-1.5 rounded-full border px-3 text-[12px] tracking-[-0.01em]',
                  'transition-[color,background-color,border-color,box-shadow] duration-200',
                  view === value
                    ? 'border-rule-strong bg-card-solid font-semibold text-fg shadow-glass'
                    : 'border-rule bg-card text-fg-muted hover:border-rule-strong hover:text-fg'
                )}
              >
                {value === 'people' ? t.title : t.access.title}
              </button>
            ))}
          </div>
        </div>

        {view === 'people' && (
          <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-2 pb-4">
            <div className="relative min-w-[13rem] flex-1 basis-[16rem]">
              <label htmlFor={searchId} className="sr-only">
                {t.search.label}
              </label>
              <Search
                size={14}
                aria-hidden
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-fg-subtle"
              />
              <Input
                id={searchId}
                value={filters.q ?? ''}
                placeholder={t.search.placeholder}
                className="pl-8"
                onChange={(event) => setFilters((current) => ({ ...current, q: event.target.value }))}
              />
              {filters.q !== '' && (
                <button
                  type="button"
                  aria-label={t.search.clear}
                  onClick={() => setFilters((current) => ({ ...current, q: '' }))}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-fg-subtle hover:text-fg"
                >
                  <X size={13} aria-hidden />
                </button>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-1.5">
              <FilterPill
                active={filters.role === '' && filters.status === ''}
                onClick={() => setFilters((current) => ({ ...current, role: '', status: '' }))}
              >
                {t.filters.all}
                {tally && <span className="readout ml-1.5 text-fg-subtle">{fmt.formatCount(tally.active)}</span>}
              </FilterPill>

              {ROLE_FILTERS.map((role) => {
                const count = tally?.byRole.find((entry) => entry.role === role)?.count ?? 0
                if (count === 0) return null
                return (
                  <FilterPill
                    key={role}
                    active={filters.role === role}
                    onClick={() =>
                      setFilters((current) => ({ ...current, role: current.role === role ? '' : role, status: '' }))
                    }
                  >
                    {t.roleLabel[role]}
                    <span className="readout ml-1.5 text-fg-subtle">{fmt.formatCount(count)}</span>
                  </FilterPill>
                )
              })}

              {tally && tally.deactivated > 0 && (
                <FilterPill
                  active={filters.status === 'deactivated'}
                  onClick={() =>
                    setFilters((current) => ({
                      ...current,
                      status: current.status === 'deactivated' ? '' : 'deactivated',
                      role: ''
                    }))
                  }
                >
                  {t.filters.deactivated}
                  <span className="readout ml-1.5 text-fg-subtle">{fmt.formatCount(tally.deactivated)}</span>
                </FilterPill>
              )}
            </div>

            {(groups.data?.items.length ?? 0) > 0 && (
              <div>
                <label htmlFor={groupId} className="sr-only">
                  {t.filters.group}
                </label>
                <Select
                  id={groupId}
                  value={group}
                  onChange={(event) => setGroup(event.target.value)}
                  className={clsx(
                    'h-[28px] max-w-[14rem] rounded-full text-[12px]',
                    group !== '' && 'border-rule-strong font-semibold'
                  )}
                >
                  <option value="">{t.filters.anyGroup}</option>
                  {(groups.data?.items ?? []).map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.name}
                    </option>
                  ))}
                </Select>
              </div>
            )}

            {/* A plain link, not a fetch: the browser's own download is what makes a CSV land
                as a file rather than as a string in memory the page then has to hand over. */}
            <a
              href={peopleExportUrl(scoped)}
              className="readout ml-auto inline-flex items-center gap-1.5 text-fg-subtle hover:text-fg"
            >
              <Download size={12} aria-hidden />
              {t.list.export}
            </a>
          </div>
        )}
      </header>

      {view === 'access' ? (
        <div className="min-h-0 flex-1 overflow-y-auto">
          <AccessPanel canWrite={canWrite} onOpenPerson={openPerson} />
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 lg:divide-x lg:divide-rule">
          {/* The list hides itself on a narrow viewport once somebody is selected: two halves
              of a 360 px column are two unusable halves. */}
          <div
            className={clsx(
              'min-h-0 flex-1 overflow-y-auto px-5 py-4 sm:px-8 lg:max-w-[26rem] lg:px-5',
              selected && 'hidden lg:block'
            )}
          >
            {people.isLoading && (
              <p className="flex items-center gap-2 text-[13px] text-fg-subtle">
                <Spinner /> {t.list.loading}
              </p>
            )}

            {people.isError && <EmptyState title={t.list.failed} body={t.list.failedBody} />}

            {!people.isLoading && !people.isError && items.length === 0 && (
              <EmptyState
                title={filters.q ? t.search.empty(filters.q) : group !== '' ? t.list.emptyGroup : t.list.empty}
                body={filters.q ? t.search.emptyBody : group !== '' ? t.list.emptyGroupBody : t.list.emptyBody}
              />
            )}

            <ul className="space-y-2">
              {items.map((item, index) => (
                <li key={item.id}>
                  <button
                    type="button"
                    aria-current={selected === item.id}
                    onClick={() => setSelected(item.id)}
                    className="w-full text-left"
                  >
                    <StudioRow
                      style={riseDelay(index)}
                      className={clsx(selected === item.id && 'border-rule-strong bg-card-solid')}
                    >
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="min-w-0 truncate text-[13.5px] font-semibold">{item.displayName}</span>
                        {item.isYou && <Badge tone="primary">{t.list.you}</Badge>}
                        {item.status === 'deactivated' && <Badge tone="alert">{t.list.deactivatedBadge}</Badge>}
                      </div>
                      <p className="mt-0.5 truncate text-[12.5px] text-fg-muted">{item.email}</p>
                      <div className="mt-2">
                        <RoleChipRow roles={item.roles} />
                      </div>
                      <RowGroups groups={item.groups} />
                      {/* Managing a group is not being in it (D-3), so it gets its own line. */}
                      <RowGroups groups={item.managedGroups} prefix={t.list.manages} />
                      <p className="readout mt-1.5 text-fg-subtle">
                        {item.lastLoginAt ? t.list.lastSeen(fmt.formatAgo(item.lastLoginAt)) : t.list.never}
                      </p>
                    </StudioRow>
                  </button>
                </li>
              ))}
            </ul>

            {people.hasNextPage && (
              <div className="mt-3">
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={people.isFetchingNextPage}
                  onClick={() => void people.fetchNextPage()}
                >
                  {people.isFetchingNextPage ? t.list.loadingMore : t.list.more}
                </Button>
              </div>
            )}
          </div>

          <div className={clsx('min-h-0 flex-1 overflow-y-auto', !selected && 'hidden lg:block')}>
            <PersonPanel
              person={person.data}
              isLoading={person.isLoading && selected !== null}
              canWrite={canWrite}
              {...(selected ? { onBack: () => setSelected(null) } : {})}
            />
          </div>
        </div>
      )}
    </div>
  )
}

const FilterPill = ({
  active,
  onClick,
  children
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) => (
  <button
    type="button"
    aria-pressed={active}
    onClick={onClick}
    className={clsx(
      'inline-flex h-[28px] items-center rounded-full border px-3 text-[12px] tracking-[-0.01em]',
      'transition-[color,background-color,border-color] duration-200',
      active
        ? 'border-rule-strong bg-card-solid font-semibold text-fg'
        : 'border-rule bg-card text-fg-muted hover:border-rule-strong hover:text-fg'
    )}
  >
    {children}
  </button>
)
